"use client";

import { TextShimmer } from "@/components/text-shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BLUR_DATA_URL } from "@/config/site";
import { encodedVisaParams } from "@/feature/visa/search/lib/visa-formatter-helper";
import { IVisaCard, IVisaSearchSchema } from "@/feature/visa/search/types/visa.types";
import { priceFormatter } from "@/lib/price-formatter";
import { absoluteUrl } from "@/lib/utils";
import { ArrowRight, CalendarClock, ShieldCheck, Users } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTransition } from "react";

interface VisaListCardProps {
  visa: IVisaCard;
  searchData: IVisaSearchSchema;
  eagerImage?: boolean;
}

function toCurrency(value: string | number | null | undefined) {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) {
    return priceFormatter(0);
  }

  return priceFormatter(amount);
}

export default function VisaListCard({
  visa,
  searchData,
  eagerImage = false,
}: VisaListCardProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const params = encodedVisaParams(searchData);
  const detailsHref = params
    ? `/visa/${encodeURIComponent(visa.slug)}?${params}`
    : `/visa/${encodeURIComponent(visa.slug)}`;

  const handleViewDetails = () => {
    startTransition(() => {
      router.push(detailsHref);
    });
  };

  return (
    <Card className="group w-full overflow-hidden rounded-2xl border border-border/60 bg-background p-0 shadow-sm transition-all duration-200 hover:shadow-md">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
          <div className="relative h-56 w-full overflow-hidden md:h-full">
            <Image
              src={absoluteUrl(
                visa.image || "https://placehold.co/600x400/png",
                true,
              )}
              alt={visa.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              sizes="(max-width: 768px) 100vw, 280px"
              quality={72}
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              loading={eagerImage ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
            <div className="absolute left-3 top-3">
              <Badge className="border border-white/30 bg-black/35 text-white hover:bg-black/35">
                {visa.country_name}
              </Badge>
            </div>
          </div>

          <div className="flex min-w-0 flex-col justify-between p-4 md:p-5">
            <div className="space-y-3">
              <h3 className="line-clamp-2 text-lg font-semibold leading-snug md:text-xl">
                {visa.title}
              </h3>
              <p className="line-clamp-2 text-sm text-muted-foreground">
                Visa processing support with document guidance and application
                readiness checks.
              </p>

              <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <p className="inline-flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 shrink-0 text-primary" />
                  <span>Max validity: {visa.max_validity} days</span>
                </p>
                <p className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4 shrink-0 text-primary" />
                  <span>
                    {searchData.travelers} traveler
                    {searchData.travelers === 1 ? "" : "s"}
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Badge variant="secondary">
                Visa Fee: {toCurrency(visa.visa_fee)}
              </Badge>
              <Badge
                variant="outline"
                className="inline-flex items-center gap-1"
              >
                <ShieldCheck className="size-3.5" />
                Processing: {toCurrency(visa.processing_fee)}
              </Badge>
            </div>
          </div>
        </div>

        <aside className="flex w-full flex-col justify-between border-l border-border/60 bg-gradient-to-b from-primary/[0.08] to-background p-4 text-right md:p-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Visa Fee
            </p>
            <p className="mt-1 text-2xl font-bold text-primary">
              {toCurrency(visa.visa_fee)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              + {toCurrency(visa.processing_fee)} processing
            </p>
          </div>

          <div className="mt-5">
            <Button
              onClick={handleViewDetails}
              className="w-full rounded-lg"
              disabled={isPending}
            >
              {isPending ? (
                <TextShimmer>View Details</TextShimmer>
              ) : (
                "View Details"
              )}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </aside>
      </div>
    </Card>
  );
}
